// apps/web/src/pages/vendas/relatorio/relatorios-recentes-painel.tsx — Fase 12, Plano 17 (PV-08, D-36, T-12-82).
// Painel de relatórios recentes do cliente: mostra o estado persistido (gerando → pronto/falha) e os downloads.
// Consulta GET /api/v1/vendas/relatorios/:id SÓ enquanto houver relatório PENDENTE/GERANDO e para ao desmontar (T-12-82).
// WR-D1-06: setTimeout encadeado (a próxima consulta só sai depois da anterior terminar), backoff nas falhas, pausa com a aba
// em segundo plano, teto de 5 minutos e falha visível com botão "Atualizar".
import { useEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import type { RelatorioPvView } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { formatarDataHora } from '../../../lib/vendas/formatacao';
import {
  FALHAS_SEGUIDAS_MAXIMAS,
  MENSAGEM_FALHA_ACOMPANHAR,
  MENSAGEM_GERACAO_DEMORANDO,
  consultaExpirou,
  deveContinuarConsultando,
  proximoIntervaloConsulta,
  rotuloEstadoRelatorio,
} from '../../../lib/vendas/relatorio';

const BASE = '/api/v1/vendas/relatorios';

interface RelatoriosRecentesPainelProps {
  readonly clienteId: string | null;
  /** Muda quando um relatório novo é criado: o painel recarrega a lista. */
  readonly atualizacao: number;
}

function formatarHora(iso: string): string {
  return Number.isNaN(new Date(iso).getTime()) ? '' : formatarDataHora(iso);
}

export function RelatoriosRecentesPainel({ clienteId, atualizacao }: RelatoriosRecentesPainelProps) {
  const [relatorios, setRelatorios] = useState<readonly RelatorioPvView[]>([]);
  const [erroLista, setErroLista] = useState(false);
  const [anuncio, setAnuncio] = useState('');
  // 'ativo' consulta; 'demorando' passou do teto; 'falha' desistiu após falhas seguidas. `tentativa` reinicia a lista e a consulta.
  const [acompanhamento, setAcompanhamento] = useState<'ativo' | 'demorando' | 'falha'>('ativo');
  const [tentativa, setTentativa] = useState(0);
  const relatoriosRef = useRef<readonly RelatorioPvView[]>([]);
  relatoriosRef.current = relatorios;

  // Carrega os recentes do cliente.
  useEffect(() => {
    if (!clienteId) {
      setRelatorios([]);
      return;
    }
    let vivo = true;
    setErroLista(false);
    void fetch(`${BASE}?clienteId=${encodeURIComponent(clienteId)}`, { credentials: 'include' })
      .then(async (res) => {
        if (!vivo) return;
        if (!res.ok) {
          setErroLista(true);
          return;
        }
        setRelatorios((await res.json()) as RelatorioPvView[]);
      })
      .catch(() => {
        if (vivo) setErroLista(true);
      });
    return () => {
      vivo = false;
    };
  }, [clienteId, atualizacao, tentativa]);

  const emGeracao = deveContinuarConsultando(relatorios.map((r) => r.estado));

  // Polling do estado persistido: só enquanto houver relatório em geração; o cleanup para ao desmontar (T-12-82).
  useEffect(() => {
    if (!emGeracao) return;
    let vivo = true;
    let timer: number | undefined;
    let falhasSeguidas = 0;
    const inicio = Date.now();
    setAcompanhamento('ativo');

    const agendar = (): void => {
      timer = window.setTimeout(() => void ciclo(), proximoIntervaloConsulta(falhasSeguidas));
    };

    async function consultar(id: string): Promise<boolean> {
      try {
        const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, { credentials: 'include' });
        if (!vivo) return true;
        if (!res.ok) return false;
        const atualizado = (await res.json()) as RelatorioPvView;
        if (!vivo) return true;
        if (atualizado.estado === 'PRONTO') setAnuncio(rotuloEstadoRelatorio('PRONTO'));
        setRelatorios((atual) => atual.map((r) => (r.id === atualizado.id ? atualizado : r)));
        return true;
      } catch {
        return false;
      }
    }

    async function ciclo(): Promise<void> {
      if (!vivo) return;
      if (consultaExpirou(inicio, Date.now())) {
        setAcompanhamento('demorando');
        return;
      }
      // Aba em segundo plano: não consulta agora (o tempo continua contando para o teto).
      if (document.visibilityState === 'hidden') {
        agendar();
        return;
      }
      const pendentes = relatoriosRef.current.filter((r) => deveContinuarConsultando([r.estado]));
      const resultados = await Promise.all(pendentes.map((pendente) => consultar(pendente.id)));
      if (!vivo) return;
      falhasSeguidas = resultados.every(Boolean) ? 0 : falhasSeguidas + 1;
      if (falhasSeguidas >= FALHAS_SEGUIDAS_MAXIMAS) {
        setAcompanhamento('falha');
        return;
      }
      agendar();
    }

    agendar();
    return () => {
      vivo = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [emGeracao, tentativa]);

  if (!clienteId) return null;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <h2 className="text-base font-semibold leading-tight">Relatórios recentes</h2>
        <p role="status" className="sr-only">
          {anuncio}
        </p>
        {erroLista && <p className="text-sm text-rose-700">Não foi possível carregar os relatórios recentes.</p>}
        {emGeracao && acompanhamento !== 'ativo' && (
          <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-amber-800">
            <span>{acompanhamento === 'demorando' ? MENSAGEM_GERACAO_DEMORANDO : MENSAGEM_FALHA_ACOMPANHAR}</span>
            <Button type="button" variant="outline" size="sm" onClick={() => setTentativa((n) => n + 1)}>
              Atualizar
            </Button>
          </div>
        )}
        {!erroLista && relatorios.length === 0 && <p className="text-sm text-muted-foreground">Nenhum relatório gerado para este cliente.</p>}
        <ul className="flex flex-col divide-y">
          {relatorios.map((relatorio) => (
            <li key={relatorio.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="flex flex-col">
                <span className="text-sm font-medium">{relatorio.clienteNome}</span>
                <span className={`text-xs ${relatorio.estado === 'FALHA' ? 'text-rose-700' : 'text-muted-foreground'}`}>
                  {rotuloEstadoRelatorio(relatorio.estado)} <span className="tabular-nums">{formatarHora(relatorio.createdAt)}</span>
                </span>
              </div>
              {relatorio.estado === 'PRONTO' && (
                <div className="flex items-center gap-2">
                  {relatorio.temPdf && (
                    <Button asChild variant="outline" size="sm">
                      <a href={`${BASE}/${encodeURIComponent(relatorio.id)}/pdf`} download>
                        <Download aria-hidden="true" />
                        Baixar PDF
                      </a>
                    </Button>
                  )}
                  {relatorio.temXlsx && (
                    <Button asChild variant="outline" size="sm">
                      <a href={`${BASE}/${encodeURIComponent(relatorio.id)}/xlsx`} download>
                        <Download aria-hidden="true" />
                        Baixar Excel
                      </a>
                    </Button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
